import { toJS } from 'mobx';
import UtilsService from '../services/UtilsService';
import lessonManager from '../managers/LessonManager';
import responseManager from '../managers/ResponseManager';
import Auth from '../services/AuthService';
import studentActivityManager from '../managers/StudentActivityManager';

const alertEmptyPreviousResponse = async () => {
  const { default: responseService } = await import('../services/ResponseService');
  return responseService.alertEmptyPreviousResponse();
};

export class Behavior {
  mustMessageParent = () =>{
    return false;
  }

  /** When true, navigation must not auto-submit; the student must press Submit. */
  requiresExplicitSubmitOnNavigation = () => false;

  /** Buffer iframe/postMessage payload until flush (Standalone Alchemie and similar). */
  holdInteractiveMessage = () => false;

  flushInteractiveMessage = () => false;

  clearInteractiveMessageSession = () => {};

  getMessage = () => {
    return {};
  }

  checkForValidResponse = (questionResponse, model) => {
    console.warn('Behavior.checkForValidResponse should be implemented by behaviors that use this method');
  }

  setCorrectAnswer = (state, model) => {
    console.warn('Behavior.checkForValidResponse should be implemented by behaviors that use this method');
  }

  resetStudentAnswer = (state) => {
    console.warn('Behavior.checkForValidResponse should be implemented by behaviors that use this method');
  }

  setResponseData = (data, questionResponse) => {
    console.warn('Behavior.setResponseData should be implemented by behaviors that use this method');
  }

  getInitializedResponse = (lessonElementId, model, activityId) => {
    console.warn('Behavior.getInitializedResponse should be implemented by behaviors that use this method');
  }

  isAutoScored = (model) => {
    console.warn('Behavior.isAutoScored should be implemented by behaviors that use this method');
  }

  getCorrectAnswersText = (model) => {
    console.warn('Behavior.getCorrectAnswersText should be implemented by behaviors that use this method');
  }

  getQuestionFeedbackText = (model, correctFeedback, incorrectFeedback, correctFeedbackBody, incorrectFeedbackBody, mode, questionIsCorrect, currentAttemptCount, { lessonElementState }) => {
    console.warn('Behavior.getQuestionFeedbackText should be implemented by behaviors that use this method');
  }

  isAnswerCorrect = (answerId, lessonElementState, model) => {
    console.warn('Behavior.isAnswerCorrect should be implemented by behaviors that use this method');
  }

  isQuestionCorrect = (lessonElementState, lessonElementId) => {
    console.warn('Behavior.isQuestionCorrect should be implemented by behaviors that use this method');
  }

  removeResponseData = () => {
    console.warn('Behavior.removeResponseData should be implemented by behaviors that use this method');
  }

  hasResponseDependency = (model, lessonElementId) => {
    const responseDependency = lessonManager.lessonOptions?.responseDependency;
    const responseDependencyValue = responseDependency && responseDependency[lessonElementId];

    if (!responseDependencyValue) {
      return false;
    }

    if (typeof responseDependencyValue === 'object') {
      if (responseDependencyValue.source === 'inLesson') {
        return responseDependencyValue.questionId;
      }
      if (responseDependencyValue.source === 'differentLesson') {
        return responseDependencyValue.lessonId &&
          responseDependencyValue.questionForeverId &&
          responseDependencyValue.lessonId !== '' &&
          responseDependencyValue.questionForeverId !== '';
      }
    }

    return responseDependencyValue !== null && responseDependencyValue !== '';
  }

  isResponsePopulated = (responseAnswer) => {
    if (!responseAnswer) {
      return false;
    }

    const content = toJS(responseAnswer);
    const hasText = (value) => UtilsService.stripTagsAndEntities(value || '').trim() !== '';
    const isUserInputFilled = (value) => {
      if (!value || typeof value !== 'object') {
        return false;
      }
      if (hasText(value.text) || hasText(value.content) || hasText(value.text?.label)) {
        return true;
      }
      if (value.answerId) {
        return true;
      }
      if (Array.isArray(value.answerIds) && value.answerIds.length > 0) {
        return true;
      }
      if (Array.isArray(value.selections) && value.selections.some((selection) => (
        selection?.answerId || hasText(selection?.text)
      ))) {
        return true;
      }
      return false;
    };

    if (hasText(content.text)) {
      return true;
    }
    if (content.drawing && Object.keys(content.drawing).length > 0) {
      return true;
    }
    if (Array.isArray(content.columns) && content.columns.some((column) => hasText(column?.text))) {
      return true;
    }

    // Prefer the live userInputMap over `prompts`; `prompts` may be a stale snapshot.
    const userInputMap = responseAnswer.userInputMap;
    const mapValues = userInputMap
      ? (typeof userInputMap.values === 'function'
        ? Array.from(userInputMap.values())
        : Object.values(toJS(userInputMap) || {}))
      : [];
    if (mapValues.length > 0) {
      return mapValues.some((value) => isUserInputFilled(value));
    }
    // Tree-shaped prompts (Relationships) keep student answers on nested `children`,
    // so flatten before checking. Flat prompt lists are unaffected.
    if (Array.isArray(content.prompts)) {
      const flatPrompts = UtilsService.flattenChildren(content.prompts) || [];
      if (flatPrompts.some((prompt) => isUserInputFilled(prompt))) {
        return true;
      }
    }

    return false;
  }

  normalizePreviousResponse = (responseAnswer) => {
    if (!responseAnswer) {
      return null;
    }

    const content = toJS(responseAnswer);
    if (responseAnswer.userInputMap) {
      const values = typeof responseAnswer.userInputMap.values === 'function'
        ? Array.from(responseAnswer.userInputMap.values())
        : Object.values(content.userInputMap || {});
      // The live userInputMap is the source of truth once it exists. A stored `prompts`
      // list can be a stale snapshot (e.g. from an earlier Show Previous Response), and
      // chained dependencies (q1 -> q2 -> q3) would otherwise read the original values.
      if (values.length > 0) {
        content.prompts = values.map((entry) => ({
          ...entry,
          id: entry?.dataId || entry?.id,
          dataId: entry?.dataId || entry?.id
        })).filter((entry) => entry.id);
      }
    }
    return content;
  }

  getPreviousResponse = (model, lessonElementId) => {
    if (!this.hasResponseDependency(model, lessonElementId)) {
      return null;
    }

    const responseDependency = lessonManager.lessonOptions?.responseDependency;
    const responseDependencyValue = responseDependency[lessonElementId];
    let previousQuestionId;

    if (typeof responseDependencyValue === 'object') {
      if (responseDependencyValue.source === 'inLesson') {
        previousQuestionId = responseDependencyValue.questionId;
      } else if (responseDependencyValue.source === 'differentLesson') {
        this.fetchPriorResponsesFromDifferentLesson(responseDependencyValue, lessonElementId);
        return null;
      } else {
        return null;
      }
    } else {
      previousQuestionId = responseDependencyValue;
    }

    const previousQuestionState = responseManager.getLessonElementState(previousQuestionId);
    if (!previousQuestionState || !previousQuestionState.currentResponseAnswer) {
      return null;
    }

    return this.normalizePreviousResponse(previousQuestionState.currentResponseAnswer);
  }

  applyPreviousResponseContent = (lessonElementId, content) => {
    const lessonElementState = responseManager.getLessonElementState(lessonElementId);
    if (!lessonElementState || !content) {
      return;
    }

    const cloned = UtilsService.safeMobxClone(content);
    cloned.lessonElementId = lessonElementId;
    lessonElementState.setCurrentResponse(cloned);
  }

  fetchPriorResponsesFromDifferentLesson = async (responseDependencyValue, lessonElementId) => {
    try {
      const { lessonId, questionForeverId } = responseDependencyValue;
      const activityId = studentActivityManager.studentActivityId;

      if (!activityId) {
        console.warn('No activity ID available for fetching prior responses');
        return;
      }

      const response = await Auth.fetch(`${Auth.ecms}/api/getPriorResponses?lessonForeverId=${lessonId}&questionForeverId=${questionForeverId}&activityId=${activityId}`, {
        method: 'GET',
      });

      if (response && response.responses && Array.isArray(response.responses)) {
        const nonEmptyResponses = response.responses.filter((resp) => {
          try {
            const content = JSON.parse(resp.content);
            return this.isResponsePopulated(content);
          } catch (e) {
            return false;
          }
        });

        if (nonEmptyResponses.length === 0) {
          alertEmptyPreviousResponse();
          return;
        }

        if (nonEmptyResponses.length === 1) {
          try {
            const content = JSON.parse(nonEmptyResponses[0].content);
            this.applyPreviousResponseContent(lessonElementId, content);
          } catch (e) {
            console.error('Error parsing single response content:', e);
            alertEmptyPreviousResponse();
          }
        } else {
          this.showResponseSelectionDialog(nonEmptyResponses, lessonElementId);
        }
      } else {
        alertEmptyPreviousResponse();
      }
    } catch (error) {
      console.error('Error fetching prior responses from different lesson:', error);
      alertEmptyPreviousResponse();
    }
  }

  populateWithPreviousResponse = (lessonElementId, model) => {
    const previousResponse = this.getPreviousResponse(model, lessonElementId);
    if (this.isResponsePopulated(previousResponse)) {
      this.applyPreviousResponseContent(lessonElementId, previousResponse);
    } else if (responseDependencyIsInLesson(lessonManager.lessonOptions?.responseDependency?.[lessonElementId])) {
      alertEmptyPreviousResponse();
    }
  }

  hasCurrentContent = (lessonElementId) => {
    const lessonElementState = responseManager.getLessonElementState(lessonElementId);
    if (!lessonElementState || !lessonElementState.currentResponseAnswer) {
      return false;
    }
    return this.isResponsePopulated(lessonElementState.currentResponseAnswer);
  }

  showResponseSelectionDialog = async (responses, lessonElementId) => {
    try {
      const { showResponseSelectionDialog } = await import('../components/dialogs/ResponseSelectionDialog');

      const responseData = responses.map((resp, index) => {
        try {
          const content = JSON.parse(resp.content);
          return {
            id: resp.id,
            index: index + 1,
            content,
            creationDate: resp.creationTimestamp,
            modificationDate: resp.modificationTimestamp,
            activityName: resp.activityName
          };
        } catch (e) {
          return null;
        }
      }).filter(Boolean);

      const selectedResponse = await showResponseSelectionDialog({
        responses: responseData,
        type: 'generic'
      });

      if (selectedResponse) {
        this.applyPreviousResponseContent(lessonElementId, selectedResponse.content);
      }
    } catch (error) {
      console.error('Error showing response selection dialog:', error);
    }
  }
}

function responseDependencyIsInLesson(responseDependencyValue) {
  if (!responseDependencyValue) {
    return false;
  }
  if (typeof responseDependencyValue === 'object') {
    return responseDependencyValue.source !== 'differentLesson';
  }
  return true;
}

